import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useClientSession } from "@/hooks/useClientSession";
import { MessageSquareQuote, Coins, ShoppingCart } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const DISMISSED_KEY = "rpgcoin_depoimento_dismissed";
const WHATSAPP_GROUP_DIALOG_EVENT = "rpgcoins:whatsapp-group-dialog";

const isWhatsAppGroupDialogOpen = () => document.body.dataset.whatsappGroupDialogOpen === "true";

const DepoimentoPendingDialog = () => {
  const { session } = useClientSession();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [pendingTx, setPendingTx] = useState<{
    id: string;
    tipo: string;
    quantidade: number;
  } | null>(null);

  useEffect(() => {
    if (!session?.contato || !session?.token) return;

    const check = async () => {
      try {
        const { data, error } = await supabase.functions.invoke("check-pending-depoimento", {
          body: { contato: session.contato, token: session.token },
        }).catch(() => ({ data: null, error: true }));
        if (error || !data?.pending?.length) return;

        // Filter out dismissed ones
        const dismissed = getDismissed();
        const available = data.pending.filter((t: any) => !dismissed.includes(t.id));
        if (available.length > 0) {
          setPendingTx(available[0]);
        }
      } catch {}
    };

    // Slight delay to not block initial render
    const timer = setTimeout(check, 3000);
    return () => clearTimeout(timer);
  }, [session?.contato, session?.token]);

  useEffect(() => {
    if (!pendingTx || open) return;

    if (!isWhatsAppGroupDialogOpen()) {
      setOpen(true);
      return;
    }

    const handleWhatsAppDialogState = (event: Event) => {
      const dialogStateEvent = event as CustomEvent<{ open?: boolean }>;
      if (!dialogStateEvent.detail?.open) {
        setOpen(true);
      }
    };

    window.addEventListener(WHATSAPP_GROUP_DIALOG_EVENT, handleWhatsAppDialogState as EventListener);

    return () => {
      window.removeEventListener(WHATSAPP_GROUP_DIALOG_EVENT, handleWhatsAppDialogState as EventListener);
    };
  }, [open, pendingTx]);

  const getDismissed = (): string[] => {
    try {
      return JSON.parse(localStorage.getItem(DISMISSED_KEY) || "[]");
    } catch {
      return [];
    }
  };

  const dismiss = () => {
    if (pendingTx) {
      const list = getDismissed();
      list.push(pendingTx.id);
      localStorage.setItem(DISMISSED_KEY, JSON.stringify(list));
    }
    setOpen(false);
    setPendingTx(null);
  };

  const goToDepoimento = () => {
    if (!pendingTx) return;

    navigate(`/depoimento/${pendingTx.id}`);
    setOpen(false);
    setPendingTx(null);
  };

  if (!pendingTx) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) dismiss(); }}>
      <DialogContent className="max-w-sm border-primary/40 bg-background" preventOutsideClose onEscapeKeyDown={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="font-pixel text-xs text-primary flex items-center gap-2">
            <MessageSquareQuote className="w-5 h-5" />
            Conte sua Experiência!
          </DialogTitle>
          <DialogDescription className="font-pixel text-[8px] text-muted-foreground">
            Deixe um depoimento e ganhe +1 RC
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <p className="font-pixel text-[8px] text-foreground/80">
            Vimos que você concluiu recentemente uma{" "}
            <span className="text-primary font-bold inline-flex items-center gap-1">
              {pendingTx.tipo === "compra" ? (
                <><ShoppingCart className="w-3 h-3 inline" /> compra</>
              ) : (
                <><Coins className="w-3 h-3 inline" /> venda</>
              )}
            </span>{" "}
            de <span className="text-primary font-bold">{pendingTx.quantidade.toLocaleString("pt-BR")} TC</span>.
          </p>
          <p className="font-pixel text-[8px] text-foreground/80">
            Que tal compartilhar como foi sua experiência? Ao ter seu depoimento aprovado, você ganha{" "}
            <span className="text-primary font-bold">+1 RC</span> de recompensa!
          </p>

          <div className="flex gap-2 pt-2">
            <button
              onClick={goToDepoimento}
              className="tibia-btn flex-1 font-pixel text-[8px] px-3 py-2"
            >
              Deixar Depoimento
            </button>
            <button
              onClick={dismiss}
              className="flex-1 font-pixel text-[8px] px-3 py-2 border border-primary/30 rounded text-muted-foreground hover:text-foreground transition-colors"
            >
              Agora Não
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DepoimentoPendingDialog;
